import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, TextFormat, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './deal.reducer';

export const Deal = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const dealList = useAppSelector(state => state.deal.entities);
  const loading = useAppSelector(state => state.deal.loading);
  const totalItems = useAppSelector(state => state.deal.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="deal-heading" data-cy="DealHeading">
        <Translate contentKey="tycheApp.deal.home.title">Deals</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tycheApp.deal.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/deal/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tycheApp.deal.home.createLabel">Create new Deal</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tycheApp.deal.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {dealList && dealList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tycheApp.deal.id">Id</Translate> <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('name')}>
                  <Translate contentKey="tycheApp.deal.name">Name</Translate> <FontAwesomeIcon icon={getSortIconByFieldName('name')} />
                </th>
                <th className="hand" onClick={sort('cashBackCurr')}>
                  <Translate contentKey="tycheApp.deal.cashBackCurr">Cash Back Curr</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cashBackCurr')} />
                </th>
                <th className="hand" onClick={sort('cashBackAmt')}>
                  <Translate contentKey="tycheApp.deal.cashBackAmt">Cash Back Amt</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cashBackAmt')} />
                </th>
                <th className="hand" onClick={sort('buzzCommission')}>
                  <Translate contentKey="tycheApp.deal.buzzCommission">Buzz Commission</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('buzzCommission')} />
                </th>
                <th className="hand" onClick={sort('publisherCommission')}>
                  <Translate contentKey="tycheApp.deal.publisherCommission">Publisher Commission</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('publisherCommission')} />
                </th>
                <th className="hand" onClick={sort('redeemPoint')}>
                  <Translate contentKey="tycheApp.deal.redeemPoint">Redeem Point</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('redeemPoint')} />
                </th>
                <th className="hand" onClick={sort('imagePath')}>
                  <Translate contentKey="tycheApp.deal.imagePath">Image Path</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('imagePath')} />
                </th>
                <th className="hand" onClick={sort('validityFrom')}>
                  <Translate contentKey="tycheApp.deal.validityFrom">Validity From</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('validityFrom')} />
                </th>
                <th className="hand" onClick={sort('validityTo')}>
                  <Translate contentKey="tycheApp.deal.validityTo">Validity To</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('validityTo')} />
                </th>
                <th className="hand" onClick={sort('type')}>
                  <Translate contentKey="tycheApp.deal.type">Type</Translate> <FontAwesomeIcon icon={getSortIconByFieldName('type')} />
                </th>
                <th className="hand" onClick={sort('category')}>
                  <Translate contentKey="tycheApp.deal.category">Category</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('category')} />
                </th>
                <th className="hand" onClick={sort('instruction')}>
                  <Translate contentKey="tycheApp.deal.instruction">Instruction</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('instruction')} />
                </th>
                <th className="hand" onClick={sort('termsAndConditions')}>
                  <Translate contentKey="tycheApp.deal.termsAndConditions">Terms And Conditions</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('termsAndConditions')} />
                </th>
                <th className="hand" onClick={sort('inventoryTracking')}>
                  <Translate contentKey="tycheApp.deal.inventoryTracking">Inventory Tracking</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('inventoryTracking')} />
                </th>
                <th className="hand" onClick={sort('stockLevel')}>
                  <Translate contentKey="tycheApp.deal.stockLevel">Stock Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('stockLevel')} />
                </th>
                <th className="hand" onClick={sort('createdAt')}>
                  <Translate contentKey="tycheApp.deal.createdAt">Created At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdAt')} />
                </th>
                <th className="hand" onClick={sort('createdBy')}>
                  <Translate contentKey="tycheApp.deal.createdBy">Created By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdBy')} />
                </th>
                <th className="hand" onClick={sort('updatedAt')}>
                  <Translate contentKey="tycheApp.deal.updatedAt">Updated At</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('updatedAt')} />
                </th>
                <th className="hand" onClick={sort('updatedBy')}>
                  <Translate contentKey="tycheApp.deal.updatedBy">Updated By</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('updatedBy')} />
                </th>
                <th>
                  <Translate contentKey="tycheApp.deal.merchant">Merchant</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tycheApp.deal.publisher">Publisher</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dealList.map((deal, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/deal/${deal.id}`} color="link" size="sm">
                      {deal.id}
                    </Button>
                  </td>
                  <td>{deal.name}</td>
                  <td>{deal.cashBackCurr}</td>
                  <td>{deal.cashBackAmt}</td>
                  <td>{deal.buzzCommission}</td>
                  <td>{deal.publisherCommission}</td>
                  <td>{deal.redeemPoint}</td>
                  <td>{deal.imagePath}</td>
                  <td>{deal.validityFrom ? <TextFormat type="date" value={deal.validityFrom} format={APP_DATE_FORMAT} /> : null}</td>
                  <td>{deal.validityTo ? <TextFormat type="date" value={deal.validityTo} format={APP_DATE_FORMAT} /> : null}</td>
                  <td>{deal.type}</td>
                  <td>{deal.category}</td>
                  <td>{deal.instruction}</td>
                  <td>{deal.termsAndConditions}</td>
                  <td>{deal.inventoryTracking ? 'true' : 'false'}</td>
                  <td>{deal.stockLevel}</td>
                  <td>{deal.createdAt ? <TextFormat type="date" value={deal.createdAt} format={APP_DATE_FORMAT} /> : null}</td>
                  <td>{deal.createdBy}</td>
                  <td>{deal.updatedAt ? <TextFormat type="date" value={deal.updatedAt} format={APP_DATE_FORMAT} /> : null}</td>
                  <td>{deal.updatedBy}</td>
                  <td>{deal.merchant ? <Link to={`/merchant/${deal.merchant.id}`}>{deal.merchant.id}</Link> : ''}</td>
                  <td>{deal.publisher ? <Link to={`/publisher/${deal.publisher.id}`}>{deal.publisher.id}</Link> : ''}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/deal/${deal.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/deal/${deal.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/deal/${deal.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tycheApp.deal.home.notFound">No Deals found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={dealList && dealList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Deal;
